import React from "react";

/**
 * CT3F Penrose-style logomark — inspired by the business card.
 * Three interlocking bars in flame gradient (red → orange → amber) forming
 * an "impossible triangle". Pure SVG, no external assets.
 */
export const LogoMark = ({ size = 36, className = "", glow = false }) => {
  const id = React.useId();
  const g1 = `ct3f-bar1-${id}`;
  const g2 = `ct3f-bar2-${id}`;
  const g3 = `ct3f-bar3-${id}`;
  const fid = `ct3f-glow-${id}`;

  return (
    <svg
      width={size}
      height={size * (172 / 200)}
      viewBox="0 0 200 172"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label="CT3F"
      className={className}
    >
      <defs>
        {/* Bar 1 — left edge (top → bottom-left). Light at top, deep red at bottom. */}
        <linearGradient id={g1} x1="100" y1="10" x2="10" y2="160" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#FBB04A" />
          <stop offset="55%" stopColor="#F25A29" />
          <stop offset="100%" stopColor="#C72A10" />
        </linearGradient>
        {/* Bar 2 — bottom edge. Amber/orange across the base. */}
        <linearGradient id={g2} x1="20" y1="158" x2="180" y2="158" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#C72A10" />
          <stop offset="50%" stopColor="#F25A29" />
          <stop offset="100%" stopColor="#F89A2B" />
        </linearGradient>
        {/* Bar 3 — right edge (top → bottom-right). Bright orange to amber. */}
        <linearGradient id={g3} x1="100" y1="10" x2="190" y2="160" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#F25A29" />
          <stop offset="55%" stopColor="#F58220" />
          <stop offset="100%" stopColor="#FBB04A" />
        </linearGradient>
        {glow && (
          <filter id={fid} x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        )}
      </defs>

      <g filter={glow ? `url(#${fid})` : undefined}>
        {/*
          Penrose tribar — three quadrilaterals.
          Order matters: each bar overlaps the next at a different corner,
          giving the "impossible triangle" illusion.
        */}

        {/* Bar 3 drawn first (so Bar 1 will overlap it at the apex). */}
        <path
          d="M 100 10 L 190 160 L 162 160 L 105 64 Z"
          fill={`url(#${g3})`}
        />
        {/* Top inner edge of Bar 3 — small bevel adding the “face” of the bar */}
        <path
          d="M 100 10 L 105 64 L 122 50 L 117 14 Z"
          fill="#7E1B0A"
          opacity="0.55"
        />

        {/* Bar 2 — bottom. Drawn second so it overlaps Bar 3's foot at right. */}
        <path
          d="M 10 160 L 190 160 L 162 130 L 38 130 Z"
          fill={`url(#${g2})`}
        />
        {/* Bottom front face of Bar 2 — subtle dark band */}
        <path
          d="M 10 160 L 190 160 L 200 172 L 0 172 Z"
          fill="#6B1607"
          opacity="0.65"
        />

        {/* Bar 1 — left edge. Drawn last so it overlaps Bar 2's foot at bottom-left,
            and visually appears in front near the apex (closing the impossibility). */}
        <path
          d="M 100 10 L 10 160 L 38 160 L 95 64 Z"
          fill={`url(#${g1})`}
        />
        {/* Apex inner face suggesting the bar continues "behind" */}
        <path
          d="M 100 10 L 95 64 L 78 50 L 83 14 Z"
          fill="#7E1B0A"
          opacity="0.55"
        />
      </g>
    </svg>
  );
};

export const LogoLockup = ({ size = 28, withSubtitle = true, className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <LogoMark size={size + 8} />
    <div className="leading-none">
      <div className="font-display font-black uppercase tracking-tight text-white text-xl md:text-2xl">
        CT<span className="text-flame">3</span>F
      </div>
      {withSubtitle && (
        <div className="label-tech text-white/40 mt-1 hidden sm:block">
          LU · FR · BE
        </div>
      )}
    </div>
  </div>
);

export default LogoMark;
